import { onClickOutside } from '../../../ts/helper';
import { FvCustomEventNames } from '../../../ts/events';
import { Combobox } from '../../atoms/atomCombobox';

const settings = {
  /** When opening the {@link theSearchElement}, the focus will jump into the `input[type="search"]`. Set this to `true` if there is some text above the input, that should be read first. The focus will then be on the searchbar instead. */
  focusOnDialog: false
};

/** The search element */
let theSearchElement: HTMLElement;

/** Returns `true` if the {@link theSearchElement} is currently open */
const isOpen = (): boolean => {
  if (!theSearchElement) throw Error("Missing required argument 'searchBar'");

  const isHidden = theSearchElement.classList.contains('isHidden');

  if (!isHidden) {
    // Let others know, that the search is now open
    const evt = new Event(FvCustomEventNames.BasisSearchOpen);
    document.dispatchEvent(evt);
  } else {
    // Let others know, that the search is now closed
    const evt = new Event(FvCustomEventNames.BasisSearchClose);
    document.dispatchEvent(evt);
  }

  return !isHidden;
};

/** Elements that control {@link theSearchElement} */
const triggers = () => {
  if (!theSearchElement) throw Error("Missing required argument 'searchBar'");
  return <(HTMLButtonElement | HTMLAnchorElement)[]>(
    Array.from(
      document.querySelectorAll(`[aria-controls="${theSearchElement.id}"]`)
    )
  );
};

/** Any element that possibly triggered {@link BasisSearchOpen} */
let activeElement: HTMLButtonElement | HTMLAnchorElement | undefined;

/**
 * Listen for keyboard input while the {@link theSearchElement} is open.
 * @param e Keydown
 */
const onKeydown = (e: KeyboardEvent) => {
  if (e.code === 'Escape') {
    BasisSearchClose(true);
  }
};

/**
 * Open or close the searchbar based on its current state
 */
export const BasisSearchToggle = () => {
  if (isOpen()) {
    BasisSearchClose();
  } else {
    BasisSearchOpen();
  }
};

/**
 * When the focus is outside of {@link theSearchElement}, close it
 * @param e Focus event
 */
const onFocusIn = (e: FocusEvent) => {
  if (!theSearchElement.contains(e.target as Node)) {
    BasisSearchClose();
  }
};

/**
 * Open the searchbar. The searchbar can be closed with the "Escpape" key (when it was opened with a click)
 * @requires {@link theSearchElement}
 */
export const BasisSearchOpen = () => {
  theSearchElement.classList.remove('isHidden');
  if (['A', 'BUTTON'].includes(document.activeElement.nodeName)) {
    activeElement =
      document.activeElement.nodeName === 'A'
        ? <HTMLAnchorElement>document.activeElement
        : <HTMLButtonElement>document.activeElement;
  }

  if (!theSearchElement) throw Error("Missing required argument 'searchBar'");

  /** Mark all triggers as 'expanded' */
  triggers().forEach((item) => {
    item.setAttribute('aria-expanded', 'true');
  });

  /** Listen for keyboard input, so we can close with ESC */
  document.addEventListener('keydown', onKeydown);

  /** Listen for focus on elments, so we can close the searchbar when the focus is outside of it */
  document.addEventListener('focusin', onFocusIn);

  onClickOutside(theSearchElement, (e) => {
    if (
      !triggers().find(
        (x) => x === <Element>e.target || x.contains(<Element>e.target)
      )
    ) {
      // Close if the clicked element is:
      // 1. Outside of theSearchElement
      // 2. Not a trigger from triggers()
      BasisSearchClose();
    }
  });

  if (settings.focusOnDialog) {
    /** Focus on dialog */
    theSearchElement.tabIndex = -1;
    setTimeout(() => {
      theSearchElement.focus();
    }, 1);
  } else {
    /** Search input */
    const input: HTMLInputElement = theSearchElement.querySelector('input');

    /** Jump into the input directly */
    setTimeout(() => {
      if (input && activeElement) input.focus();
    }, 1);
  }
};

/**
 * Close the searchbar
 * @requires {@link theSearchElement}
 * @param focus If `true` will focus on the {@link activeElement} after animation is done
 */
export const BasisSearchClose = (focus?: boolean) => {
  if (!theSearchElement) return;

  theSearchElement.classList.add('isHidden');
  document.removeEventListener('keydown', onKeydown);
  document.removeEventListener('focusin', onFocusIn);

  /** Mark all triggers as not 'expanded' */
  triggers().forEach((item) => {
    item.setAttribute('aria-expanded', 'false');
  });

  if (focus && activeElement) {
    activeElement.tabIndex = -1;

    setTimeout(() => {
      activeElement.focus();
      activeElement.removeAttribute('tabindex');
      activeElement = undefined;
    }, 1);
  }
};

/**
 * Friendation component
 * @param component Instance of this _Friendation_ component
 */
const BasisSearch = (component: HTMLElement) => {
  theSearchElement = component;

  /** Search input */
  const searchInput = <HTMLInputElement>(
    theSearchElement.querySelector('input[type="search"]')
  );

  /** Close button. Clicking it will close the searchbar */
  const closeButton = <HTMLButtonElement>(
    component.querySelector('.closeButton')
  );

  /**
   * Each element in DOM with `aria-controls="${this.id}"` will toggle {@link component}
   */
  const handleTriggers = () => {
    triggers().forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
      item.setAttribute('aria-haspopup', 'true');
      item.addEventListener('click', BasisSearchToggle);
    });
  };

  /**
   * Focussing on the search input will open the searchbar
   * @param e Focus event
   */
  const onSearchInputFocus = (e: Event) => {
    e.preventDefault();
    BasisSearchOpen();
  };

  /**
   * React to the close button being clicked
   * @param e Click event
   */
  const onCloseButtonClick = (e: MouseEvent) => {
    e.preventDefault();
    if (window.innerWidth >= 1280) {
      // Desktop
      activeElement = document.querySelector(
        '[is="basis-pre-header"] .desktop [aria-controls="search"]'
      );
    } else {
      // Mobile
      activeElement = document.querySelector(
        '[is="basis-pre-header"] .mobile [aria-controls="search"]'
      );
    }
    BasisSearchClose(true);
  };

  /** Listen for header going non-visible */
  document.addEventListener(FvCustomEventNames.BasisHeaderOut, () => {
    BasisSearchClose();
  });

  /** Listen for Event being fired on `document` */
  document.addEventListener(
    FvCustomEventNames.BasisMobileNavigationOpens,
    () => {
      BasisSearchClose();
    }
  );

  /** remove hidden because we handle the visibility with a class toggle. We only had this here on page load to avoid content shifting */
  theSearchElement.hidden = false;

  /** Close search on {@link closeButton} click */
  closeButton.addEventListener('click', onCloseButtonClick.bind(this));

  /** Listen for focus on search input */
  searchInput.addEventListener('focus', onSearchInputFocus);

  const targetPage = () => {
    const form = <HTMLFormElement>theSearchElement.querySelector('form');

    const url = new URL(form.action);
    url.searchParams.set('q', searchInput.value.trim());
    url.hash = '';
    return url.href;
  };

  new Combobox({
    input: searchInput,
    minLength: 3,
    targetPage,
    asyncSelector: '.results-list > li'
  });

  handleTriggers();
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="basis-search"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void = BasisSearch;

/**
 * Friendation component
 */
export const aSearch = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
